import { ResponsiveCirclePacking } from "@nivo/circle-packing";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { getKnowledgeGraphHierarchy, getWorkspaceHierarchy, type HierarchyNode } from "@/api/knowledgeGraphApi";

/** Nivo circle packing node: id, value (for size), label (for tooltip), children. */
interface NivoNode {
	id: string;
	value: number;
	label: string;
	children?: NivoNode[];
}

function mapToNivoNode(node: HierarchyNode): NivoNode {
	return {
		id: node.id,
		value: node.instanceCount || 1,
		label: node.label,
		...(node.children?.length ? { children: node.children.map(mapToNivoNode) } : {}),
	};
}

// Nivo requires a single root datum — wrap the real class nodes under a transparent sentinel.
function buildNivoData(roots: HierarchyNode[]): NivoNode {
	return {
		id: "__root__",
		value: 0,
		label: "",
		children: roots.map(mapToNivoNode),
	};
}

// Palette similar to nivo's default "nivo" scheme, used for all non-root nodes.
const PALETTE = ["#e8c1a0", "#f47560", "#f1e15b", "#e8a838", "#61cdbb", "#97e3d5", "#f0a3a3", "#8dd3c7"];

function pickColor(id: string): string {
	let h = 5381;
	for (let i = 0; i < id.length; i++) h = ((h << 5) + h + id.charCodeAt(i)) & 0xffffffff;
	return PALETTE[Math.abs(h) % PALETTE.length];
}

interface HierarchyViewProps {
	graphId?: string | null;
	workspaceId?: string | null;
}

const HierarchyView: React.FC<HierarchyViewProps> = ({ graphId, workspaceId }) => {
	const id = workspaceId ?? graphId;
	const isWorkspace = Boolean(workspaceId);

	const { data, isLoading, isError, error } = useQuery({
		queryKey: isWorkspace ? ["workspace-hierarchy", workspaceId] : ["knowledge-graph-hierarchy", graphId],
		queryFn: () =>
			isWorkspace ? getWorkspaceHierarchy(workspaceId as string) : getKnowledgeGraphHierarchy(graphId as string),
		enabled: Boolean(id),
	});

	if (!id) {
		return (
			<div className="flex h-full items-center justify-center p-6">
				<p className="text-sm text-muted">Select a workspace to view its class hierarchy.</p>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center p-6">
				<Loader2 className="h-8 w-8 animate-spin text-muted" aria-hidden />
				<span className="sr-only">Loading hierarchy...</span>
			</div>
		);
	}

	if (isError || error) {
		return (
			<div className="flex h-full items-center justify-center p-6">
				<p className="text-sm text-red-500">{error instanceof Error ? error.message : "Failed to load hierarchy"}</p>
			</div>
		);
	}

	const roots = data?.roots ?? [];
	if (roots.length === 0) {
		return (
			<div className="flex h-full items-center justify-center p-6">
				<p className="text-sm text-muted">
					No class hierarchy for this graph. Run the RDF pipeline to extract an ontology.
				</p>
			</div>
		);
	}

	const nivoData = buildNivoData(roots);

	return (
		<div className="h-full w-full min-h-[300px]" role="img" aria-label="Class hierarchy packed circles">
			<ResponsiveCirclePacking
				data={nivoData}
				id="id"
				value="value"
				colors={(node) => (node.id === "__root__" ? "transparent" : pickColor(node.id))}
				borderColor={(node) => (node.id === "__root__" ? "transparent" : "rgba(0,0,0,0.15)")}
				padding={4}
				enableLabels
				labelsSkipRadius={12}
				label={(node) => (node.id === "__root__" ? "" : (node.data as NivoNode).label)}
				tooltip={({ id, value, data }) => (
					<div className="rounded border border-subtle bg-surface px-2 py-1 text-sm shadow">
						{id !== "__root__" && (
							<>
								<strong className="text-primary">{(data as NivoNode).label ?? id}</strong>
								{value != null && <span className="ml-1 text-muted">({value})</span>}
							</>
						)}
					</div>
				)}
				theme={{
					text: {
						fill: "var(--color-primary, currentColor)",
						fontSize: 12,
					},
				}}
			/>
		</div>
	);
};

export default HierarchyView;
